/**
 * "Something to do here" counts shared by the hub's nav tiles and the in-run HUD menu icons:
 * unspent talent points of the main hero and a pending vault reward. Pure.
 */
import type { ClassId } from '@keystone/sim';
import { type Profile, gridPoints, itemById, itemScore, pointsSpent, profileStanding, unequippedItems, vaultPending } from '@keystone/progression';

/**
 * +grid: unspent Waystone Grid points of the hero (talents are retired from live play). Profiles
 * without a character (legacy tools) fall back to the old talent count.
 */
export function unspentPoints(p: Profile, classId: ClassId = p.mainClassId): number {
  const ch = p.characters[classId];
  if (!ch) return 0;
  if (p.character?.classId === classId) return gridPoints(p, classId).free; // +grid
  const lo = ch.loadouts.find((l) => l.id === ch.activeLoadoutId);
  return Math.max(0, ch.hero.talentPoints - pointsSpent(lo?.nodes ?? []));
}

export interface MenuBadges {
  /** Unspent talent points of the main hero (0 = no badge). */
  talents: number;
  /** A vault cache is waiting (resource offer, or a legacy item vault). */
  vault: boolean;
}

export function menuBadges(p: Profile): MenuBadges {
  return { talents: unspentPoints(p), vault: vaultPending(p) };
}

/** Rank standing key ("champion:2"); the lobby Rank dot shows until the player has seen it. */
export function standingKey(p: Profile): string {
  const s = profileStanding(p);
  return `${s.tier}:${s.subRank}`;
}

/** A bag item scores higher than what the main hero wears in its slot (legendaries skipped). */
export function bagUpgrade(p: Profile, classId: ClassId = p.mainClassId): boolean {
  const ch = p.characters[classId];
  if (!ch) return false;
  for (const it of unequippedItems(p)) {
    if (it.legendaryId) continue;
    const cur = itemById(p, ch.gear[it.slot]);
    if (!cur || itemScore(it, classId) > itemScore(cur, classId) + 1e-6) return true;
  }
  return false;
}

export interface NavDots {
  hero: boolean;
  skills: boolean;
  forge: boolean;
  rank: boolean;
}

/**
 * Lobby nav red dots: Hero (a bag item beats the equipped piece), Skills (unspent talent points),
 * Forge (`forgeBadge` > 0, from forgeAttention), Rank (a standing the player has not seen).
 */
export function navDots(p: Profile, o: { forgeBadge: number; rankSeen: string | null }): NavDots {
  return {
    hero: bagUpgrade(p),
    skills: unspentPoints(p) > 0,
    forge: o.forgeBadge > 0,
    rank: o.rankSeen !== null && o.rankSeen !== standingKey(p),
  };
}
